import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ListTodo, RefreshCw, Phone } from 'lucide-react'

import {
  useFollowUps,
  type FollowUp,
  type FollowUpStatus,
} from '../api/followups'
import { formatPhone, timeAgo } from '../lib/utils'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'

const STATUS_TABS: FollowUpStatus[] = ['open', 'completed']
type Scope = 'all' | 'me'

function truncate(value: string | null | undefined, max = 70): string {
  if (!value) return ''
  return value.length > max ? `${value.slice(0, max - 1)}…` : value
}

function StatusBadge({ status }: { status: FollowUpStatus }) {
  const { t } = useTranslation('followups')
  const style =
    status === 'completed'
      ? 'bg-success/20 text-success'
      : 'bg-warning/20 text-warning'
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${style}`}
    >
      {t(`status.${status}`)}
    </span>
  )
}

function assignmentLabel(row: FollowUp): string {
  if (!row.assigned_extension_id) return ''
  const parts: string[] = []
  if (row.assigned_extension_label) parts.push(row.assigned_extension_label)
  const sub: string[] = []
  if (row.assigned_location_label) sub.push(row.assigned_location_label)
  if (row.assigned_extension) sub.push(`ext ${row.assigned_extension}`)
  if (sub.length) parts.push(`(${sub.join(' · ')})`)
  return parts.join(' ')
}

export function FollowUpsPage() {
  const { t } = useTranslation('followups')
  const navigate = useNavigate()
  const [status, setStatus] = useState<FollowUpStatus>('open')
  const [scope, setScope] = useState<Scope>('all')

  const { data, isLoading, isError, refetch, isFetching } = useFollowUps({
    status,
    assignee: scope === 'me' ? 'me' : undefined,
  })

  const rows = useMemo(() => data?.follow_ups ?? [], [data?.follow_ups])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-text-primary md:text-2xl">
            {t('list.title')}
          </h1>
          <p className="mt-1 text-sm text-text-secondary">{t('list.subtitle')}</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-elevated px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-card disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          {t('list.refresh')}
        </button>
      </div>

      {/* Primary tabs: status */}
      <div className="flex gap-2 border-b border-border">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setStatus(tab)}
            className={`-mb-px rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
              status === tab
                ? 'bg-primary text-white'
                : 'bg-surface-elevated text-text-muted hover:bg-surface-card'
            }`}
          >
            {t(`tabs.${tab}`)}
          </button>
        ))}
      </div>

      {/* Secondary filter: all / assigned to me */}
      <div className="inline-flex rounded-lg border border-border bg-surface-elevated p-0.5 text-sm">
        {(['all', 'me'] as Scope[]).map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              scope === s
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            {t(`scope.${s}`)}
          </button>
        ))}
      </div>

      {isLoading && <SpinLoader label={t('list.loading')} />}
      {isError && (
        <ErrorCard message={t('errors.loadFailed')} onRetry={() => refetch()} />
      )}

      {data && rows.length === 0 && (
        <div className="rounded-lg border border-border bg-surface-card p-8 text-center">
          <ListTodo className="mx-auto h-10 w-10 text-text-muted" />
          <p className="mt-3 text-sm text-text-muted">{t(`empty.${status}`)}</p>
        </div>
      )}

      {data && rows.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-surface-card md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="px-4 py-3">{t('columns.requester')}</th>
                  <th className="px-4 py-3">{t('columns.task')}</th>
                  <th className="px-4 py-3">{t('columns.assigned')}</th>
                  <th className="px-4 py-3">{t('columns.created')}</th>
                  <th className="px-4 py-3">{t('columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <FollowUpRow
                    key={row.id}
                    row={row}
                    onClick={() => navigate(`/app/follow-ups/${row.id}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {rows.map((row) => (
              <FollowUpCard
                key={row.id}
                row={row}
                onClick={() => navigate(`/app/follow-ups/${row.id}`)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function FollowUpRow({ row, onClick }: { row: FollowUp; onClick: () => void }) {
  const { t } = useTranslation('followups')
  const assigned = assignmentLabel(row)
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-border/50 transition-colors hover:bg-surface-elevated/50"
    >
      <td className="px-4 py-3">
        <div className="font-medium text-text-primary">
          {row.requester_name || (
            <span className="italic text-text-muted">{t('list.noName')}</span>
          )}
        </div>
        {row.requester_phone && (
          <div className="font-mono text-xs text-text-muted">
            {formatPhone(row.requester_phone)}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-text-secondary" title={row.task || undefined}>
        {truncate(row.task, 70)}
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {assigned || <span className="text-text-muted">{t('list.unassigned')}</span>}
      </td>
      <td className="px-4 py-3 text-text-muted">{timeAgo(row.created_at)}</td>
      <td className="px-4 py-3">
        <StatusBadge status={row.status} />
      </td>
    </tr>
  )
}

function FollowUpCard({ row, onClick }: { row: FollowUp; onClick: () => void }) {
  const { t } = useTranslation('followups')
  const assigned = assignmentLabel(row)
  return (
    <div
      onClick={onClick}
      className="cursor-pointer rounded-lg border border-border bg-surface-card p-4 transition-colors hover:bg-surface-elevated/50"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="font-medium text-text-primary">
            {row.requester_name || (
              <span className="italic text-text-muted">{t('list.noName')}</span>
            )}
          </div>
          {row.requester_phone && (
            <div className="mt-0.5 flex items-center gap-1.5 font-mono text-xs text-text-muted">
              <Phone className="h-3 w-3" />
              {formatPhone(row.requester_phone)}
            </div>
          )}
        </div>
        <StatusBadge status={row.status} />
      </div>
      <div className="mt-2 text-sm text-text-secondary">{truncate(row.task, 90)}</div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-text-muted">
        <span>
          {assigned || t('list.unassigned')}
        </span>
        <span>{timeAgo(row.created_at)}</span>
      </div>
    </div>
  )
}
